import { useEffect, useState } from "react";
import { listApptSlots, type ApptSlot } from "./appointments-api";

export function venueDay(timeZone: string, now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  return ["year", "month", "day"].map(key => parts.find(p => p.type === key)!.value).join("-");
}
export function venueDays(timeZone: string, now = new Date()): string[] {
  const today = venueDay(timeZone, now);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today + "T12:00:00Z");
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
}
export function validateSlotZone(zone: unknown): string {
  if (typeof zone !== "string" || !zone.trim()) throw new Error("Availability did not provide a timezone. Please retry.");
  try { venueDay(zone); } catch { throw new Error("Availability returned an invalid timezone. Please retry."); }
  return zone;
}

/** The first read discovers the slot venue. Its slots stay hidden until the civil day agrees. */
export function useAppointmentSlots({ enabled, scope, service, locationId, initialDate }: {
  enabled: boolean; scope: string; service?: string; locationId?: string | null; initialDate?: string;
}) {
  const [selection, setSelection] = useState<{ scope: string; date: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const selected = selection?.scope === scope ? selection.date : initialDate;
  const key = JSON.stringify([enabled, scope, service, locationId, selected, attempt]);
  const [result, setResult] = useState<{ key: string; date: string; zone: string; slots: ApptSlot[]; error: string | null } | null>(null);
  useEffect(() => {
    if (!enabled || !service) { setResult(null); if (!enabled) setSelection(null); return; }
    let current = true;
    const run = async () => {
      const provisional = selected || new Date().toISOString().slice(0, 10);
      let response = await listApptSlots({ service, locationId, date: provisional });
      if (!current) return;
      const zone = validateSlotZone(response.timezone);
      const date = selected || venueDay(zone);
      if (date !== provisional) {
        response = await listApptSlots({ service, locationId, date });
        if (!current) return;
        if (validateSlotZone(response.timezone) !== zone) throw new Error("Availability timezone changed. Please retry.");
      }
      if (current) setResult({ key, date, zone, slots: response.slots, error: null });
    };
    void run().catch(e => { if (current) setResult({ key, date: selected || "", zone: "", slots: [], error: e instanceof Error ? e.message : String(e) }); });
    return () => { current = false; };
  }, [key]);
  const active = result?.key === key ? result : null;
  return {
    date: selected || active?.date || "",
    timezone: active?.zone || "",
    days: active?.zone ? venueDays(active.zone) : [],
    slots: active && !active.error ? active.slots : null,
    error: active?.error || null,
    setDate: (date: string) => setSelection({ scope, date }),
    retry: () => setAttempt(n => n + 1),
  };
}
